import FormField from 'components/FormField';
import KoalaButton from 'koala/components/Button';
import KoalaInputValidation from 'koala/components/InputValidation';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryCache } from 'react-query';
import theme from 'theme';
import * as remoteApi from 'api/remote';
import styled from 'styled-components';
import queryKeys from 'config/queryKeys';
import { Outcome } from 'types';

const Table = styled.table`
  margin: ${theme.spacing.x2} 0px;
  border-collapse: collapse;
  width: 100%;
`;

const Td = styled.td<{ isSelected: boolean }>`
  white-space: nowrap;
  padding: ${theme.spacing.half};
  border: 1px solid ${theme.colors.inputBorder};
  background-color: ${({ isSelected }) => (isSelected ? theme.colors.T20 : 'transparent')};
`;

const Tr = styled.tr``;

interface Props {
  workspaceSlug: string;
  outcome: Outcome;
}

function ChartSourceType(props: Props) {
  const { workspaceSlug, outcome } = props;
  const { t } = useTranslation();
  const translationKey = 'modals.dataConnectors.amplitudeData';
  const [error, setError] = useState('');
  const [table, setTable] = useState<any>(null);
  const [chartId, setChartId] = useState(outcome.data_source_meta?.chart_id || '');
  const [selectedRow, setSelectedRow] = useState<number>(outcome.data_source_meta?.row - 1 || 0);
  const [selectedColumn, setSelectedColumn] = useState<number>(outcome.data_source_meta?.column || -1);
  const [applyPercentageFormatting, setApplyPercentageFormatting] = useState<boolean>(false);
  // const [currentTab, setCurrentTab] = useState<string>(outcome.data_source_meta?.column || 'current');
  const queryCache = useQueryCache();

  const params = {
    workspaceSlug,
    chartId,
  };

  const [loadChartDataMutation, { isLoading: isFetchingChartData }] = useMutation(
    () => remoteApi.fetchAmplitudeChart('amplitude_chart', workspaceSlug, params),
    {
      onSuccess: (response) => {
        setError('');
        const chartData = response?.data?.chart_data;
        setTable(chartData);
      },
      onError: (error: any) => {
        if (error.response.data.error) {
          setError(error.response.data.error);
        } else {
          setError('An error occurred while fetching the chart data');
        }
      },
    },
  );

  const [updateOutcomeDataSourceMutation, { isLoading: isUpdating }] = useMutation(remoteApi.updateOutcomeDataSource, {
    onSuccess: () => {
      queryCache.invalidateQueries(queryKeys.currentOutcome);
    },
  });

  const handleUpdate = (e: any) => {
    e.preventDefault();

    let body = {
      data_source_origin: 'amplitude',
      data_source_type: 'chart',
      data_source_meta: {
        chart_id: chartId,
        row: selectedRow,
        column: selectedColumn,
        multiply_by_100: applyPercentageFormatting,
      },
    };

    const mutationParams = {
      outcomeId: outcome.nano_slug,
      body,
    };
    updateOutcomeDataSourceMutation(mutationParams);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setChartId(e.target.value);
  };

  const handleLoadChartData = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setTable(null);
    // setChartData(null);
    setSelectedRow(1);
    loadChartDataMutation();
  };

  const handleSelectRow = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedRow(parseInt(e.target.value));
  };

  const handleSelectColumn = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedColumn(parseInt(e.target.value));
  };

  return (
    <>
      <FormField>
        <label>{t(`${translationKey}.chartId`)}</label>
        <input value={chartId} onChange={handleInputChange} placeholder="Chart ID" className="small" />
        <small style={{ marginTop: theme.spacing.x1 }}>{t(`${translationKey}.chartIdInfo`)}</small>
        {error && <KoalaInputValidation state="error" text={error} />}
      </FormField>

      <KoalaButton
        onClick={(e) => handleLoadChartData(e)}
        disabled={!chartId || isFetchingChartData}
        loading={isFetchingChartData}
        appearance="subtle"
        type="button"
      >
        {t(`${translationKey}.loadChartData`)}
      </KoalaButton>
      {table && (
        <>
          <Table cellPadding="6">
            <tbody>
              {table.map((row: any, i: number) => (
                <Tr key={i}>
                  {row &&
                    row.map((cell: any, j: number) => {
                      const columnSelected = j === row.length - 1 + selectedColumn;
                      const isSelected = selectedRow === i && columnSelected;
                      return (
                        <Td key={j} isSelected={isSelected}>
                          {cell}
                        </Td>
                      );
                    })}
                </Tr>
              ))}
            </tbody>
          </Table>

          <FormField>
            <label>Select row</label>
            <select value={selectedRow} onChange={handleSelectRow}>
              {table.slice(1).map((row: any, i: number) => (
                <option key={i} value={i + 1}>
                  {row?.[0]}
                </option>
              ))}
            </select>
          </FormField>
          <FormField>
            <label>Select column</label>
            <select value={selectedColumn} onChange={handleSelectColumn}>
              <option value={0}>Last column</option>
              <option value={-1}>Second to last column</option>
              <option value={-2}>Third to last column</option>
            </select>
          </FormField>
          <FormField>
            <label>Apply percentage formatting</label>
            <input
              type="checkbox"
              checked={applyPercentageFormatting}
              onChange={(e) => setApplyPercentageFormatting(e.target.checked)}
            />
            <span>Multiple the value by 100 (useful for percentage columns)</span>
          </FormField>
          <KoalaButton
            onClick={handleUpdate}
            loading={isUpdating}
            disabled={selectedRow === 0 || isUpdating}
            appearance="subtle"
          >
            {t(`panels.editOutcome.savePreview`)}
          </KoalaButton>
        </>
      )}
    </>
  );
}

export default ChartSourceType;
